/**
 * The assistant, in a drawer on every screen.
 *
 * It is a box you talk to, and it can do what you could have done yourself —
 * so "keep these links as design inspiration" is a sentence rather than four
 * clicks. What it did is shown under its answer in small type, because an
 * assistant that quietly changed something is worse than no assistant.
 *
 * It remembers now. This component used to hold the only copy of the
 * conversation and post the whole thing back on every turn, which meant
 * closing the tab was the end of it; the comment here used to say that was on
 * purpose because nobody had asked for a chat log. Somebody asked. So a
 * conversation is a thread on the server, belonging to the person who had it,
 * and this reopens on the last one and can go back through the others.
 *
 * What is sent is now just the new message and which thread it belongs to. The
 * history comes out of the thread rather than out of this component — which is
 * not only tidier but is the difference between a record and a copy: a browser
 * that can rewrite what it claims was said can put words in somebody's mouth
 * and then ask the assistant to act on them.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { History, Plus, Send, Sparkles, Trash2, X } from 'lucide-react'
import { api, ApiError } from '../api'
import { cn } from '../lib/cn'
import { cheer, DID } from '../lib/hapiMood'
import { useAppData } from '../context/AppDataContext'

export interface Turn {
  role: 'user' | 'assistant'
  content: string
  /** What it ran, on its own turns. */
  steps?: { tool: string; ok: boolean; said: string }[]
}

interface Answer {
  reply: string
  steps: { tool: string; ok: boolean; said: string }[]
  provider: string
  thread_id: string
}

/** One of the person's past conversations, as the list reads it. */
interface Thread {
  id: string
  title: string | null
  started_at: string
  last_at: string
  messages: number
}

/** A message as the server kept it, which is not quite the shape on screen. */
interface Kept {
  id: string
  role: 'user' | 'assistant'
  body: string
  steps: { tool: string; ok: boolean; said: string }[]
  provider: string | null
  created_at: string
}

const OPENERS = [
  'Keep these links as design inspiration',
  "What is due for La Kova this week?",
  "Who has not filed anything today?",
]

/**
 * Whether a turn changed anything, from what it ran.
 *
 * The assistant can do what you can do, which means it can create a project —
 * and the screen behind it went on showing the list it had fetched before,
 * until somebody reloaded the page. Asking it to make something and then not
 * finding it is the single worst thing this drawer can do, because the natural
 * reading is that it did not work and the natural response is to make it twice.
 *
 * The test is inverted on purpose: a turn is a read only if *every* tool it
 * used is one of the known read verbs. An unrecognised verb counts as a write,
 * so a tool added later refreshes needlessly rather than silently going stale.
 */
const READS = new Set(['list', 'get', 'find', 'search', 'file'])

function changedSomething(steps: { tool: string; ok: boolean }[]): boolean {
  return steps.some((s) => s.ok && !READS.has(s.tool.split('_')[1] ?? ''))
}

/** `buckets_add` is not a sentence. "Added to a bucket" is. */
function inWords(tool: string): string {
  const [area = '', what = ''] = tool.split('_')
  const verb: Record<string, string> = {
    list: 'Looked through',
    get: 'Opened',
    create: 'Started something in',
    add: 'Added to',
    update: 'Changed',
    archive: 'Put away in',
    file: 'Read a file from',
    editItem: 'Edited something in',
    removeItem: 'Removed something from',
  }
  const area_ = area.charAt(0).toUpperCase() + area.slice(1)
  return what === '' ? area_ : `${verb[what] ?? 'Used'} ${area_.toLowerCase()}`
}

/** "Tuesday", "3 Oct" — enough to find a conversation again, no more. */
function when(iso: string): string {
  const then = new Date(iso)
  if (Number.isNaN(then.getTime())) return ''
  const days = Math.floor((Date.now() - then.getTime()) / 86_400_000)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 7) return then.toLocaleDateString(undefined, { weekday: 'long' })
  return then.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

export function AssistantDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { reload } = useAppData()
  const [turns, setTurns] = useState<Turn[]>([])
  const [threadId, setThreadId] = useState<string | null>(null)
  const [threads, setThreads] = useState<Thread[]>([])
  const [past, setPast] = useState(false)
  const [draft, setDraft] = useState('')
  const [thinking, setThinking] = useState(false)
  const [trouble, setTrouble] = useState<string | null>(null)
  const bottom = useRef<HTMLDivElement>(null)
  const box = useRef<HTMLTextAreaElement>(null)
  // Whether this drawer has ever been opened. The last conversation is fetched
  // once, on the first open, rather than on mount: the drawer is mounted on
  // every screen for everybody, and most sessions never open it.
  const woken = useRef(false)

  const listThreads = useCallback(async () => {
    try {
      setThreads(await api<Thread[]>('/chat/threads'))
    } catch {
      // A list of old chats is the least important thing on this screen. If it
      // cannot be fetched, the drawer still works for the conversation you are
      // about to have, which is what you opened it for.
    }
  }, [])

  const load = useCallback(async (id: string) => {
    setTrouble(null)
    try {
      const kept = await api<Kept[]>(`/chat/threads/${encodeURIComponent(id)}`)
      setTurns(kept.map((m) => ({ role: m.role, content: m.body, steps: m.steps })))
      setThreadId(id)
      setPast(false)
    } catch (err) {
      setTrouble(
        err instanceof ApiError ? err.message : 'That conversation would not open. Try again.',
      )
    }
  }, [])

  useEffect(() => {
    if (!open) return
    box.current?.focus()
    if (woken.current) return
    woken.current = true
    void (async () => {
      await listThreads()
      // Carry on where they left off. Picking up the most recent rather than
      // starting blank is the whole of what "it remembers" feels like from the
      // outside; a drawer that opens empty every time has a database behind it
      // that nobody can tell is there.
      try {
        const mine = await api<Thread[]>('/chat/threads', { query: { limit: 1 } })
        const last = mine[0]
        if (last !== undefined) await load(last.id)
      } catch {
        // Same as above: an empty drawer is a working drawer.
      }
    })()
  }, [open, listThreads, load])

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: 'smooth' })
  }, [turns, thinking])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  async function send(text: string) {
    const said = text.trim()
    if (said === '' || thinking) return
    setTurns((prev) => [...prev, { role: 'user', content: said }])
    setDraft('')
    setTrouble(null)
    setThinking(true)
    cheer(DID['thinking']!)
    try {
      const answer = await api<Answer>('/chat', {
        method: 'POST',
        body: { say: said, ...(threadId === null ? {} : { threadId }) },
      })
      setThreadId(answer.thread_id)
      setTurns((prev) => [
        ...prev,
        { role: 'assistant', content: answer.reply, steps: answer.steps },
      ])
      cheer({ pose: 'idle' })
      void listThreads()
      // The screens behind this drawer are holding data fetched before the
      // assistant touched anything. Refetch when it did.
      if (changedSomething(answer.steps)) reload()
    } catch (err) {
      setTrouble(
        err instanceof ApiError ? err.message : 'That did not get through. Try again in a moment.',
      )
      cheer(DID['wentWrong']!)
    } finally {
      setThinking(false)
    }
  }

  /** A blank one. Nothing is deleted — the old thread is in the list. */
  function fresh() {
    setTurns([])
    setThreadId(null)
    setTrouble(null)
    setPast(false)
    box.current?.focus()
  }

  async function forget(id: string) {
    try {
      await api(`/chat/threads/${encodeURIComponent(id)}`, { method: 'DELETE' })
      setThreads((prev) => prev.filter((t) => t.id !== id))
      if (id === threadId) fresh()
    } catch (err) {
      setTrouble(err instanceof ApiError ? err.message : 'That would not delete. Try again.')
    }
  }

  return (
    <>
      <div
        className={cn(
          'fixed inset-0 z-[190] bg-black/30 backdrop-blur-[2px] transition-opacity',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={onClose}
        aria-hidden
      />
      <aside
        role="dialog"
        aria-label="Assistant"
        className={cn(
          'fixed bottom-0 right-0 top-0 z-[200] flex w-full max-w-md flex-col border-l border-shell-border bg-shell shadow-2xl transition-transform duration-200',
          open ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        <header className="flex h-16 shrink-0 items-center gap-1 border-b border-shell-border px-4">
          <Sparkles size={17} className="text-brand-500" />
          <h2 className="mr-auto text-sm font-semibold text-shell-text-active">Ask Hapi</h2>

          <button
            onClick={() => {
              setPast((p) => !p)
              if (!past) void listThreads()
            }}
            aria-label="Past conversations"
            title="Past conversations"
            className={cn(
              'rounded-lg p-2 text-shell-text hover:bg-shell-border/50',
              past && 'bg-shell-border/60 text-shell-text-active',
            )}
          >
            <History size={17} />
          </button>
          {turns.length > 0 && (
            <button
              onClick={fresh}
              aria-label="New conversation"
              title="New conversation"
              className="rounded-lg p-2 text-shell-text hover:bg-shell-border/50"
            >
              <Plus size={17} />
            </button>
          )}
          <button
            onClick={onClose}
            aria-label="Close the assistant"
            className="rounded-lg p-2 text-shell-text hover:bg-shell-border/50"
          >
            <X size={18} />
          </button>
        </header>

        {past ? (
          <div className="flex-1 overflow-y-auto scrollbar-thin px-4 py-5">
            {threads.length === 0 ? (
              <p className="text-sm text-shell-text">
                Nothing yet. Conversations show up here once you have had one.
              </p>
            ) : (
              <ul className="flex flex-col gap-1">
                {threads.map((t) => (
                  <li key={t.id} className="group flex items-center gap-1">
                    <button
                      onClick={() => void load(t.id)}
                      className={cn(
                        'min-w-0 flex-1 rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-shell-border/40',
                        t.id === threadId && 'bg-shell-border/60',
                      )}
                    >
                      <span className="block truncate text-shell-text-active">
                        {t.title ?? 'Untitled'}
                      </span>
                      <span className="block text-[11px] text-shell-text">
                        {when(t.last_at)} · {t.messages} message{t.messages === 1 ? '' : 's'}
                      </span>
                    </button>
                    <button
                      onClick={() => void forget(t.id)}
                      aria-label={`Delete "${t.title ?? 'Untitled'}"`}
                      title="Delete this conversation"
                      className="shrink-0 rounded-lg p-2 text-shell-text opacity-0 transition-opacity hover:bg-shell-border/50 hover:text-red-500 focus:opacity-100 group-hover:opacity-100"
                    >
                      <Trash2 size={15} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {trouble !== null && (
              <p className="mt-3 rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2 text-xs text-red-500">
                {trouble}
              </p>
            )}
          </div>
        ) : (
          <div className="flex-1 space-y-4 overflow-y-auto scrollbar-thin px-4 py-5">
            {turns.length === 0 && (
              <div className="space-y-3">
                <p className="text-sm text-shell-text">
                  Ask about the work, or tell it to keep something. It can do what you can do.
                </p>
                <div className="flex flex-col items-start gap-2">
                  {OPENERS.map((opener) => (
                    <button
                      key={opener}
                      onClick={() => void send(opener)}
                      className="rounded-full border border-shell-border px-3 py-1.5 text-left text-xs text-shell-text transition-colors hover:border-brand-500/50 hover:text-shell-text-active"
                    >
                      {opener}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {turns.map((turn, i) => (
              <div key={i} className={cn('flex', turn.role === 'user' ? 'justify-end' : 'justify-start')}>
                <div
                  className={cn(
                    'max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm',
                    turn.role === 'user'
                      ? 'bg-brand-500 text-white'
                      : 'border border-shell-border bg-shell-border/20 text-shell-text-active',
                  )}
                >
                  <p className="whitespace-pre-wrap">{turn.content}</p>
                  {turn.steps !== undefined && turn.steps.length > 0 && (
                    <ul className="mt-2 space-y-0.5 border-t border-shell-border pt-2 text-[11px] text-shell-text">
                      {turn.steps.map((step, s) => (
                        <li key={s} className={cn(!step.ok && 'text-red-500')}>
                          {step.ok ? inWords(step.tool) : step.said}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}

            {thinking && (
              <div className="flex justify-start">
                <div className="flex items-center gap-1.5 rounded-2xl border border-shell-border bg-shell-border/20 px-3.5 py-3">
                  {[0, 1, 2].map((d) => (
                    <span
                      key={d}
                      className="h-1.5 w-1.5 animate-pulse rounded-full bg-shell-text"
                      style={{ animationDelay: `${d * 150}ms` }}
                    />
                  ))}
                </div>
              </div>
            )}

            {trouble !== null && (
              <p className="rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2 text-xs text-red-500">{trouble}</p>
            )}

            <div ref={bottom} />
          </div>
        )}

        <div className="shrink-0 border-t border-shell-border p-3">
          <div className="flex items-end gap-2">
            <textarea
              ref={box}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                // Enter sends; shift-enter is a new line, the way every other
                // box people use all day behaves.
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  void send(draft)
                }
              }}
              rows={1}
              placeholder="Ask, or tell it to keep something…"
              className="max-h-32 min-h-[40px] flex-1 resize-none rounded-lg border border-shell-border bg-shell px-3 py-2.5 text-sm text-shell-text-active placeholder:text-shell-text focus:border-brand-500 focus:outline-none"
            />
            <button
              onClick={() => void send(draft)}
              disabled={thinking || draft.trim() === ''}
              aria-label="Send"
              className="rounded-lg bg-brand-500 p-2.5 text-white transition-opacity disabled:opacity-40"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      </aside>
    </>
  )
}
